import { Component, Input, OnDestroy, OnInit } from '@angular/core';
import { AbstractControl, FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import { SessionService } from '@edl-development/edl-common';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { StateService } from '@uirouter/core';
import { ToastrService } from 'ngx-toastr';
import { Subscription, firstValueFrom } from 'rxjs';
import { BaseUserRoleDTO } from 'src/app/model/base/base-user-role-dto.model';
import { ObservationTypeValues } from 'src/app/model/enum/observation-type-values.enum';
import { ObservationTypes } from 'src/app/model/enum/observation-types.enum';
import { RubricDirectionType } from 'src/app/model/enum/rubric/rubric-direction-type.enum';
import { UserRoleHelper } from 'src/app/model/helpers/user-role/user-role.helper';
import { CreateObservationDTO } from 'src/app/model/observation/diff/diff-create-dto.model';
import { FormalCreateDto } from 'src/app/model/observation/formal/formal-create-dto.model';
import { ObservationSummaryDTO } from 'src/app/model/observation/observation-summary-dto.model';
import { RubricCreateDTO } from 'src/app/model/observation/rubric/rubric-create-dto.model';
import { SloObservationDetails } from 'src/app/model/observation/slo/slo-details.model';
import { PresetDTO } from 'src/app/model/presets/preset-dto.model';
import { UserSummaryPermissions } from 'src/app/model/observation/user-summary-permisions.model';
import { WalkthroughObservationModel } from 'src/app/model/observation/walkthrough-details.model';
import { ObservationListService } from 'src/app/services/observation-list.service';
import { AnecdotalObservationService } from 'src/app/services/Observation/anecdotal-observation.service';
import { DiffService } from 'src/app/services/Observation/diff/diff.service';
import { FormalService } from 'src/app/services/Observation/formal/formal.service';
import { SloObservationService } from 'src/app/services/Observation/slo-observation.service';
import { SpmService } from 'src/app/services/Observation/spm.service';
import { WalkthroughObservationService } from 'src/app/services/Observation/walkthrough-observation.service';
import { PaetepUserHelperService } from 'src/app/services/paetep-user-helper/paetep-user-helper.service';
import { PermissionsHelperService } from 'src/app/services/permissions-helper/permissions-helper.service';
import { RubricService } from 'src/app/services/rubric/rubric.service';
import { CustomValidators } from 'src/app/validators/custom-validators';
import { RatingFormCreateDTO } from './../../model/observation/rating-form/rating-form-create.model';
import { RatingFormService } from './../../services/Observation/ratingform-service';
import { CustomFormsService } from 'src/app/services/Observation/custom-forms/custom-forms.service';
import { NtpeService } from 'src/app/services/Observation/ntpe/ntpe.service';
import { PresetManagementService } from 'src/app/services/preset-management-services/preset-management-service.service';
import { SupervisorUserDTO } from 'src/app/model/base/supervisor-user-dto.model';
import { PermissionTypes } from 'src/app/model/enum/permission-types.enum';

export const DEFAULT_USER_ROLE_ID = '-1';

@Component({
    selector: 'app-create-observation',
    templateUrl: './create-observation.component.html',
    styleUrls: ['./create-observation.component.scss']
})
export class CreateObservationComponent implements OnInit, OnDestroy {
    @Input() currentUserSummaryTab?: number;
    @Input() targetUserId?: string;
    @Input() targetUserName?: string;
    @Input() targetUserRoles: BaseUserRoleDTO[] = []
    @Input() userSummaryPermissions?: UserSummaryPermissions;
    @Input() currentUser?: string;

    loadingEvaluators: boolean = false;
    evaluators: SupervisorUserDTO[] = [];
    filteredEvaluators: SupervisorUserDTO[] = [];
    ratingFormOptions: Array<{ value: string, label: string }> = [];
    filteredTargetUserRoles: BaseUserRoleDTO[] = [];
    schoolYears: string[] = [];
    filteredSchoolYears: string[] = [];
    activeUser?: string;
    observationForm: FormGroup;
    currentObservationType: any;

    presets: PresetDTO[] = [];
    filteredPresets: PresetDTO[] = [];

    private subscriptions: Subscription[] = [];

    PermissionTypes = PermissionTypes;
    DEFAULT_USER_ROLE_ID = DEFAULT_USER_ROLE_ID;

    private options: { value: string, label: string, observationTypeValues: ObservationTypeValues[] }[] = [
        { value: "1", label: "Walkthrough", observationTypeValues: [ObservationTypeValues.Walkthrough] },
        { value: "2", label: "Formal Observation", observationTypeValues: [ObservationTypeValues.Formal] },
        { value: "3", label: "Differentiated Observation", observationTypeValues: [ObservationTypeValues.Differentiated] },
        { value: "4", label: "Anecdotal Note", observationTypeValues: [ObservationTypeValues.Anecdotal] },
        { value: "5", label: "NTP Observation", observationTypeValues: [ObservationTypeValues.NTPE] },
        { value: "6", label: "SPM/IEP/SLO", observationTypeValues: [ObservationTypeValues.SPM, ObservationTypeValues.IEP, ObservationTypeValues.SLO] },
        { value: "7", label: "Performance Goal", observationTypeValues: [ObservationTypeValues.PerformanceGoal] },
        { value: "8", label: "Stand-Alone Rubric", observationTypeValues: [ObservationTypeValues.Rubric] },
        { value: "9", label: "Custom Form", observationTypeValues: [ObservationTypeValues.CustomForm] },
        { value: "10", label: "13 Rating Form", observationTypeValues: [ObservationTypeValues.X82] },
    ];

    public filteredOptions: { value: string, label: string, observationTypeValues: ObservationTypeValues[] }[] = [];


    // TODO Move this list to a more accessible location
    private performanceMeasureTypes: { value: string, label: string, observationTypeValue: ObservationTypeValues }[] = [
        { value: "130", label: "Student Performance Measure", observationTypeValue: ObservationTypeValues.SPM },
        { value: "129", label: "IEP Goals Progress", observationTypeValue: ObservationTypeValues.IEP },
        { value: "33", label: "Student Learning Objective", observationTypeValue: ObservationTypeValues.SLO },
        // { value: "128", label: "PG" }
    ];

    public filteredPerformanceMeasureTypes: { value: string, label: string, observationTypeValue: ObservationTypeValues }[] = []

    readonly rubricDirectionTypes = [
        { value: RubricDirectionType.ONE_DIRECTIONAL, label: 'Supervisor Only Rubric' },
        { value: RubricDirectionType.BI_DIRECTIONAL, label: 'Employee/Supervisor Rubric' }
    ]

    constructor(
        private formBuilder: FormBuilder,
        private sessionService: SessionService,
        private observationListService: ObservationListService,
        private permissionsHelperService: PermissionsHelperService,
        public activeModal: NgbActiveModal,
        private stateService: StateService,
        private toastr: ToastrService,
        public anecdotalObservationService: AnecdotalObservationService,
        public formalService: FormalService,
        public diffService: DiffService,
        public sloObservationService: SloObservationService,
        public ntpeObservationService: NtpeService,
        public spmService: SpmService,
        public rubricService: RubricService,
        public ratingFormService: RatingFormService,
        public paetepUserHelperService: PaetepUserHelperService,
        public walkthroughObservationService: WalkthroughObservationService,
        private customFormsService: CustomFormsService,
        private presetManagementService: PresetManagementService,
    ) {
        this.schoolYears = this.sessionService.GetSessionValue('school_years') as string[];
        this.filteredSchoolYears = this.schoolYears;
        let schoolYear = this.schoolYears[0];

        this.observationForm = this.formBuilder.group({
            ObservationType: [this.observationTypeDefault(), [Validators.required, CustomValidators.OptionListValidator(this.options.map(x => x.value))]],
            SchoolYear: [schoolYear, [Validators.required, CustomValidators.OptionListValidator(this.schoolYears)]],
            PerformanceMeasureType: [null, this.IsRequiredValidator(["6"])],
            Evaluator: [null, this.IsRequiredValidator(["4", "5", "6", "7", "9"])],
            RubricDirectionType: [null, this.IsRequiredValidator(["8"])],
            UserRole: [null, this.IsRequiredValidator(["2", "3", "5", "8"])],
            SubType: [null, this.IsRequiredValidator(["10"])],
            AdditionalText: ["", []],
            Preset: [null, []],
            CustomFormSelector: this.formBuilder.group({
                Paging: this.formBuilder.group({
                    Ascending: [true, []],
                    OrderBy: ['title', []],
                    SearchBy: ['', []],
                    CurrentPage: [1, []]
                }),
                CustomFormId: ["", this.IsRequiredValidator(["9"])],
                CustomFormTitle: ["", []]
            })
        });

        let s = this.observationForm.get('ObservationType')?.valueChanges.subscribe(() => {
            this.observationForm.get('ObservationType')?.updateValueAndValidity({ onlySelf: true, emitEvent: false });
            this.observationForm.get('SchoolYear')?.updateValueAndValidity({ onlySelf: true, emitEvent: false });
            this.observationForm.get('PerformanceMeasureType')?.updateValueAndValidity({ onlySelf: true, emitEvent: false });
            this.observationForm.get('Evaluator')?.updateValueAndValidity({ onlySelf: true, emitEvent: false });
            this.observationForm.get('UserRole')?.updateValueAndValidity({ onlySelf: true, emitEvent: false });
            this.observationForm.get('SubType')?.updateValueAndValidity({ onlySelf: true, emitEvent: false });
            this.observationForm.get('Preset')?.updateValueAndValidity({ onlySelf: true, emitEvent: false });
            this.observationForm.get(['CustomFormSelector', 'CustomFormId'])?.updateValueAndValidity({ onlySelf: true, emitEvent: false });
            this.observationForm.get('CustomFormSelector')?.updateValueAndValidity({ onlySelf: true, emitEvent: false });
        });
        this.subscriptions.push(s!);
    }

    ngOnInit(): void {
        this.filteredOptions = this.options.filter(o => this.permissionsHelperService.canCreateObservation(o.observationTypeValues, this.userSummaryPermissions));
        this.filteredPerformanceMeasureTypes = this.performanceMeasureTypes.filter(o => this.permissionsHelperService.canCreateObservation([o.observationTypeValue], this.userSummaryPermissions));
        this.filteredTargetUserRoles = this.filteredUserRoles(this.targetUserRoles);

        this.loadData();
    }

    ngOnDestroy(): void {
        this.subscriptions.forEach((sub: Subscription) => {
            sub.unsubscribe();
        });
    }

    async loadData() {
        await this.loadEvaluators().then(() => {
            this.observationForm.controls['Evaluator'].addValidators([CustomValidators.OptionListValidator(this.evaluators.map(x => x.user.userID))]);
            if (this.evaluators.findIndex(x => x.user.userID == this.sessionService.user?.UserID) > -1) {
                this.observationForm.controls['Evaluator'].setValue(this.sessionService.user?.UserID);
            }
        });
        this.loadRatingFormOptions();
        this.loadPresets();
    }

    /**
     * User Roles are used to determine which NTPE Resource File to use on each type of observation
     * The options include the default (Highest Priority Non-NTPE Role) and all NTPE Roles the user may have
     *
     * @param userRoles
     * @returns The highest priority Non-NTPE role and all other NTPE roles
     */
    private filteredUserRoles = (userRoles: BaseUserRoleDTO[]) => {
        // Removes roles we do not want the user to be able to select
        const filteredUserRoles = UserRoleHelper.filterUserRoles(userRoles);

        const nonNTPERoles = filteredUserRoles.filter(r => !r.isNTEP);
        const ntpeRoles = filteredUserRoles.filter(r => r.isNTEP);

        // The user can choose between their highest priority NON-NTPE Role and any NTPE Roles they have
        return [...nonNTPERoles.slice(0, 1), ...ntpeRoles];
    }

    private IsRequiredValidator = (observationTypes: string[] = []) => {
        return (formControl: AbstractControl) => {
            const root = formControl.root;
            const observationType = root?.get('ObservationType')?.value;
            if (observationTypes?.includes(observationType)) {
                return Validators.required(formControl);
            }

            return null;
        }
    }

    private async loadEvaluators() {
        this.loadingEvaluators = true;
        await this.paetepUserHelperService.getSelectEvaluatorOptions(
            this.targetUserId as string, 
            true,
            this.userSummaryPermissions?.observationsTargetUserCanHave ?? [],
        ).then((evaluators) => {
            this.evaluators = evaluators;
            this.updateFilteredEvaluators();
        }).finally(() => {
            this.loadingEvaluators = false;
        })
    }

    private get selectedObservationTypeValue(): ObservationTypeValues | undefined {
        const obserationTypes = this.options.find(o => o.value == this.observationForm.get('ObservationType')?.value)?.observationTypeValues ?? [];

        if (obserationTypes.length <= 1) {
            return obserationTypes[0]
        }

        const subObservationTypes = this.performanceMeasureTypes.find(o => o.value == this.observationForm.get('PerformanceMeasureType')?.value)?.observationTypeValue;
        return subObservationTypes;
    }

    private updateFilteredEvaluators() {
        this.filteredEvaluators = this.evaluators.filter(e => e.observationsSupervisorCanManage.some(value => value == this.selectedObservationTypeValue)) ?? [];

        const evaluatorControl = this.observationForm.get('Evaluator') as FormControl;
        if (this.filteredEvaluators.findIndex(x => x.user.userID == evaluatorControl?.value) == -1) {
            evaluatorControl.setValue(null);
            evaluatorControl.updateValueAndValidity();
        }
    }

    private loadRatingFormOptions() {
        this.ratingFormOptions = this.ratingFormService.getValidFormTypes(this.targetUserRoles);
    }

    private async loadPresets() {
        await firstValueFrom(this.presetManagementService.getPresets(true))
            .then((presets) => {
                this.presets = presets;
            });
    }

    private observationTypeDefault(): string {
        switch (+(this.currentUserSummaryTab ?? 0)) {
            case 1: return "6"; //set up an alternative if this user is a supervisor
            case 2: return "1";
            case 3: return "2"; // or three?
            case 4: return "4";
            case 5: return "9";
            case 6: return "10";
            default: return "0";
        }
    }

    public shouldShowRubricTypePicker() {
        // Standalone Rubric
        return this.observationForm?.get('ObservationType')?.value === '8';
    }

    public shouldShowRolePicker() {
        // Standalone Rubric
        return this.observationForm?.get('ObservationType')?.value === '8'
            // Formal
            || this.observationForm?.get('ObservationType')?.value === '2'
            // Diff
            || this.observationForm?.get('ObservationType')?.value === '3'
            // NTPE
            || this.observationForm?.get('ObservationType')?.value === '5';
    }

    public showAdditionalText() {
        // Standalone Rubric
        return (this.observationForm?.get('ObservationType')?.value === '8' && this.observationForm?.get('RubricDirectionType')?.value == RubricDirectionType.BI_DIRECTIONAL)
            // Diff
            || this.observationForm?.get('ObservationType')?.value === '3';
    }

    showAdditionalOptionsSPM(): boolean {
        return this.observationForm?.get('ObservationType')?.value === "6";
    }

    showRatingFormOptions(): boolean {
        return this.observationForm?.get('ObservationType')?.value === "10";
    }

    /**
     * Shows the preset picker only once the item type has narrowed to a single observation type, because presets are
     * filtered per measure type and the list would otherwise be empty.
     *
     * @returns True when the item type offers presets and its observation type has resolved.
     */
    showPresetOptions(): boolean {
        return (this.observationForm?.get('ObservationType')?.value === "6" ||
                this.observationForm?.get('ObservationType')?.value === "7")
            && this.hasResolvedObservationType();
    }

    onObservationTypeChange(value: any) {
        this.currentObservationType = value;

        // reset optional form values
        this.observationForm.controls['RubricDirectionType'].setValue(null);
        this.observationForm.controls['UserRole'].setValue(null);
        this.observationForm.controls['SubType'].setValue(null);
        this.observationForm.controls['AdditionalText'].setValue("");
        this.observationForm.controls['Preset'].setValue(null);

        this.filteredSchoolYears = this.currentObservationType == 10 ? this.filterRatingFormYears() : this.schoolYears;
        this.observationForm.get('SchoolYear')?.setValue(this.filteredSchoolYears[0]);
        this.filterPresets();

        // if there is only one item in the filteredUserroles, then set the value of the userRole to that item
        if (this.filteredTargetUserRoles.length == 1) {
            this.observationForm.controls['UserRole'].setValue(this.filteredTargetUserRoles[0].ntpeLookupID ?? DEFAULT_USER_ROLE_ID);
        }

        this.updateFilteredEvaluators();
    }

    filterRatingFormYears() {
        return this.schoolYears.slice(0, 2);
    }

    filterPresets() {
        let obType = this.options.filter(o => o.value == this.observationForm.get('ObservationType')?.value)[0];
        if (obType.value === "6") {
            let performanceMeasureType = this.observationForm.get('PerformanceMeasureType')?.value;
            let checkType = parseInt(performanceMeasureType);
            this.filteredPresets = this.presets.filter(p => p.type == checkType);
        }
        else {
            this.filteredPresets = this.presets.filter(p => p.type == obType.observationTypeValues[0]);
        }

    }

    onSubmit(form: FormGroup) {
        if (form.invalid) {
            this.observationForm.markAllAsTouched();
            return;
        }

        let observationSummary = {
            evalType: -1,
            schoolYear: form.controls['SchoolYear'].value as string,
            targetUserId: this.targetUserId as string,
            evalUserId: form.controls['Evaluator'].value as string,
        }

        const userRoleId = form.controls['UserRole'].value === DEFAULT_USER_ROLE_ID ? null : form.controls['UserRole'].value;

        let obsType = form.controls['ObservationType'].value as number;
        switch (+obsType) {
            case 1:
                observationSummary.evalType = ObservationTypes.Walkthrough.Type;
                this.walkthroughObservationService
                    .createWalkthrough(observationSummary as WalkthroughObservationModel)
                    .subscribe({
                        next: (newObservation) => {
                            this.navigateOnCreation(newObservation);
                        },
                        error: (err) => {
                            console.log(err);
                            this.toastr.error(err);
                            this.observationForm?.markAsPristine();
                        },
                        complete: () => { this.toastr.success("Walkthrough Created.") }
                    });
                break;
            case 2:
                observationSummary.evalType = ObservationTypes.Formal.Type;
                observationSummary['userRoleId'] = userRoleId;
                this.formalService
                    .createFormal(observationSummary as FormalCreateDto)
                    .subscribe({
                        next: (newObservation: ObservationSummaryDTO) => {
                            this.navigateOnCreation(newObservation);
                        },
                        error: (err: string | undefined) => {
                            this.toastr.error(err);
                            this.observationForm?.markAsPristine();
                        },
                        complete: () => { this.toastr.success("Formal Created.") }
                    });
                break;
            // Diff
            case 3:
                observationSummary.evalType = ObservationTypes.Differentiated.Type;
                observationSummary['userRoleId'] = userRoleId;
                observationSummary['additionalText'] = form.controls['AdditionalText']?.value ?? '';
                this.diffService
                    .createDiff(observationSummary as CreateObservationDTO)
                    .subscribe({
                        next: (newObservation: ObservationSummaryDTO) => {
                            this.activeModal.close(),
                                this.currentUser == this.targetUserId ? this.navigateOnCreation(newObservation) : this.navigateToUserSummary();
                        },
                        error: (err: string | undefined) => {
                            this.toastr.error(err);
                            this.observationForm?.markAsPristine();
                        },
                        complete: () => { this.toastr.success("Differentiated Created.") }
                    });
                break;
            case 4:
                observationSummary.evalType = ObservationTypes.Anecdotal.Type;
                this.anecdotalObservationService
                    .createAnecdotal(observationSummary as CreateObservationDTO)
                    .subscribe({
                        next: (newObservation) => {
                            this.navigateOnCreation(newObservation);
                        },
                        error: (err) => {
                            console.log(err);
                            this.toastr.error(err);
                            this.observationForm?.markAsPristine();
                        },
                        complete: () => { this.toastr.success("Anecdotal Created.") }
                    });
                break;
            // NTPE
            case 5:
                observationSummary.evalType = ObservationTypes.NTPE.Type;
                observationSummary['userRoleId'] = userRoleId;
                observationSummary['additionalText'] = form.controls['AdditionalText']?.value ?? '';
                this.ntpeObservationService
                    .createNtpe(observationSummary as CreateObservationDTO)
                    .subscribe({
                        next: (newObservation) => {
                            this.navigateOnCreation(newObservation);
                        },
                        error: (err) => {
                            this.toastr.error(err);
                            this.observationForm?.markAsPristine();
                        },
                        complete: () => { this.toastr.success("NTPE Created.") }
                    });
                break;
            case 6:
            case 7:
                observationSummary.evalType = obsType == 7 ? ObservationTypeValues.PerformanceGoal : form.controls['PerformanceMeasureType'].value as number;
                observationSummary['presetId'] = form.controls['Preset'].value as string;
                const evalType = ObservationTypes.GetObservationType(+observationSummary.evalType);
                switch (evalType?.Type) {
                    case ObservationTypes.SPM.Type:
                    case ObservationTypes.IEP.Type:
                    case ObservationTypes.PG.Type:
                        this.spmService.createSpm(observationSummary as CreateObservationDTO)
                            .subscribe({
                                next: (newObservation) => {
                                    this.navigateOnCreation(newObservation);
                                },
                                error: (err: string | undefined) => {
                                    this.toastr.error(err);
                                    this.observationForm?.markAsPristine();
                                },
                                complete: () => { this.toastr.success(`${evalType?.Name} Created.`) }
                            });
                        break;
                    case ObservationTypes.SLO.Type:
                        this.sloObservationService
                            .createSlo(observationSummary as SloObservationDetails)
                            .subscribe({
                                next: (newObservation) => {
                                    this.navigateOnCreation(newObservation)
                                },
                                error: (err) => {
                                    console.log(err);
                                    this.toastr.error(err);
                                    this.observationForm?.markAsPristine();
                                },
                                //todo add proper observation label name
                                complete: () => { this.toastr.success("Observation Created.") }
                            });
                        break;
                    default: break;
                }
                break;
            case 8:
                observationSummary.evalType = ObservationTypes.Rubric.Type;
                observationSummary['userRoleId'] = userRoleId;
                observationSummary['directionType'] = form.controls['RubricDirectionType'].value as string;
                observationSummary['additionalText'] = form.controls['AdditionalText']?.value ?? '';
                this.rubricService
                    .createRubric(observationSummary as RubricCreateDTO)
                    .subscribe({
                        next: (newRubric?: ObservationSummaryDTO) => {
                            this.navigateOnCreation(newRubric);
                        },
                        error: (err: string | undefined) => {
                            console.log(err);
                            this.toastr.error(err);
                            this.observationForm?.markAsPristine();
                        },
                        //todo add proper observation label name
                        complete: () => { this.toastr.success("Observation Created.") }
                    });
                break;
            case 9:
                observationSummary.evalType = ObservationTypes.CustomForm.Type;
                observationSummary['presetId'] = form?.get(['CustomFormSelector', 'CustomFormId'])?.value as string;
                firstValueFrom(
                    this.customFormsService.createUserForm(observationSummary as CreateObservationDTO)
                ).then((newObservation) => {
                    this.toastr.success("Custom Form Created.")
                    this.navigateOnCreation(newObservation);
                }).catch(() => {
                    this.observationForm?.markAsPristine();
                });
                break;
            case 10:
                observationSummary['subType'] = form.get("SubType")?.value;
                this.ratingFormService
                    .createRatingForm(observationSummary as RatingFormCreateDTO)
                    .subscribe({
                        next: (ratingId) => {
                            this.activeModal.close(),
                                this.stateService
                                    .go('authenticated.user-summary.rating-form.details', {
                                        observationId: ratingId,
                                        userId: this.targetUserId
                                    });

                        },
                        error: (err) => {
                            console.log(err);
                            this.toastr.error(err);
                            this.observationForm?.markAsPristine();
                        },
                        //todo add proper observation label name
                        complete: () => { this.toastr.success("Observation Created.") }
                    });
                break;
            default:
                this.toastr.error("That observation type is not yet supported.", "Unsupported Type");
                break;
        }
    }

    private navigateOnCreation(observationSummary?: ObservationSummaryDTO, destinationTabIndex?: number, extraParams: { [paramName: string]: any } = {}) {
        this.activeModal.close();
        this.observationListService.navigateOnCreation(this.targetUserId, observationSummary, destinationTabIndex, extraParams);
    }

    private navigateToUserSummary() {
        this.stateService.go('authenticated.user-summary.view', { userId: this.targetUserId }, { reload: true });
    }

    /**
     * Tells whether the current selections narrow down to a single observation type.
     *
     * SPM/IEP/SLO covers three observation types, so it only narrows once a Performance Measure Type is chosen.
     * Every other item type is already a single type. Undefined and null are checked explicitly so that a
     * legitimate enum value of 0 still counts as resolved.
     *
     * @returns True when a single observation type is known for the current selections.
     */
    private hasResolvedObservationType(): boolean {
        return this.selectedObservationTypeValue !== undefined
            && this.selectedObservationTypeValue !== null;
    }

    /**
     * Decides whether the "no evaluators available" alert should be visible.
     *
     * An empty evaluator list before the observation type has resolved is undecidable, not a real "no evaluators"
     * result, so the alert waits for the type to resolve.
     *
     * @returns True when evaluators have loaded, the observation type has resolved, and no evaluator is eligible.
     */
    shouldShowNoEvaluatorsAlert(): boolean {
        return this.filteredEvaluators.length === 0
            && !this.loadingEvaluators
            && this.hasResolvedObservationType();
    }

    /**
     * Shows the evaluator picker only for item types that allow choosing an evaluator, and only once the item type
     * has narrowed to a single observation type so the list is never a required field with no entries.
     *
     * @returns True when the evaluator picker should be rendered.
     */
    showEvaluatorOptions(): boolean {
        if (!this.userSummaryPermissions?.canChooseThirdPartyEvaluator || !this.hasResolvedObservationType()) {
            return false;
        }

        return (this.currentObservationType === "4" &&  this.sessionService.user?.UserID == this.targetUserId) ||
            this.currentObservationType === "5" ||
            this.currentObservationType === "6" ||
            this.currentObservationType === "7" ||
            this.currentObservationType === "9";
    }
}
